import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Alert, AlertDescription, Button, RadioGroup } from '@parrot-software/design-system';
import { ChevronRight, Info as InfoIcon } from '@parrot-software/design-system/icons';

import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { CustomerStates, PaymentMethodTypes } from '@subscriptions/domain/types';
import { WizardStepFooter } from '@subscriptions/presentation/components/base';
import {
  isReactivationOrigin,
  resolveWizardOrigin,
} from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { WizardSteps } from '@subscriptions/presentation/constants';
import { useSubscriptionAnalytics, useSubscriptionBlock } from '@subscriptions/presentation/hooks';
import { getDaysToPaymentDate } from '@subscriptions/presentation/utils';

import { CardPaymentMethodCard, CARD_PAYMENT_METHOD_VALUE } from './CardPaymentMethodCard';
import { PaymentMethodCardsSkeleton } from './PaymentMethodCardsSkeleton';
import { SpeiPaymentMethodCard, SPEI_PAYMENT_METHOD_VALUE } from './SpeiPaymentMethodCard';

/**
 * Identifier of a payment-method option selectable in the method-selection step.
 */
export type PaymentMethodValue = typeof SPEI_PAYMENT_METHOD_VALUE | typeof CARD_PAYMENT_METHOD_VALUE;

/**
 * Resolves the option that must be preselected once the block has loaded.
 *
 * A reactivation preselects the card whatever the block carries. Otherwise a
 * block with advance invoicing can only be paid by SPEI, so
 * SPEI is preselected and the card option gets disabled; a block already
 * configured with any payment method preselects the card option (SPEI gets
 * disabled in that case), and a block with no payment method leaves the
 * selection empty so nothing is marked by default.
 *
 * @param block - Loaded subscription block (or undefined while fetching)
 * @param isReactivation - Whether the wizard was entered to reactivate a churned subscription
 * @returns The preselected method value, or undefined when nothing is selected
 */
const resolveInitialSelectedMethod = (
  block: SubscriptionBlockViewDto | undefined,
  isReactivation: boolean,
): PaymentMethodValue | undefined => {
  if (isReactivation) return CARD_PAYMENT_METHOD_VALUE;
  if (block?.advanceInvoice) return SPEI_PAYMENT_METHOD_VALUE;
  return block?.paymentMethod ? CARD_PAYMENT_METHOD_VALUE : undefined;
};

type BlockCreationStepPaymentProps = {
  blockUuid: string;
  onContinue: (method: PaymentMethodValue) => void;
};

/**
 * Method-selection step of the block creation wizard (step 2).
 *
 * Renders a `RadioGroup` with one card per supported payment method (SPEI and
 * credit/debit card). When the block is already configured with an active SPEI
 * method, the SPEI card is disabled and the card option is preselected; otherwise
 * both options are enabled and SPEI is the default selection. Entered to reactivate
 * a churned subscription the card is preselected.
 *
 * This step only captures the user's choice — it does not trigger Stripe or the
 * SPEI reference. On Continuar it hands the selected method up via `onContinue`;
 * the fiscal-data step (step 3) is where the choice is consumed to start the
 * Stripe session (CARD) or the SPEI reference (SPEI).
 *
 * @param props - Component properties
 * @returns React element rendering the step
 */
export const BlockCreationStepPayment = ({ blockUuid, onContinue }: BlockCreationStepPaymentProps) => {
  const { t } = useTranslation('subscriptions');
  const { search } = useLocation();
  const { data: block, isLoading: isLoadingBlock } = useSubscriptionBlock(blockUuid);

  // A reactivation overrides advance invoicing, so the card option never gets disabled.
  //
  // Outside a reactivation, advance invoicing disables the card option (SPEI is the only valid
  // method). Both can apply at once, leaving every option disabled — the hint below explains why.
  const { trackPaymentStarted, trackPaymentStepViewed } = useSubscriptionAnalytics();
  const isReactivation = isReactivationOrigin(search);
  const requiresSpeiOnly = !isReactivation && block?.advanceInvoice === true;
  const isCardDisabled = requiresSpeiOnly;
  // A block already paid by SPEI cannot select it again. The method has to still be active: an
  // expired reactivation CLABE stays the block's main method, and treating it as current would
  // leave the customer unable to ask for a new one.
  const isSpeiDisabled =
    block?.paymentMethod?.methodType === PaymentMethodTypes.SPEI &&
    block.paymentMethod.status === CustomerStates.ACTIVE;
  const bothDisabled = isCardDisabled && isSpeiDisabled;

  const resolveHintKey = (): string | undefined => {
    if (!requiresSpeiOnly) return undefined;
    return bothDisabled
      ? 'blockCreationWizard.payment.bothDisabledHint'
      : 'blockCreationWizard.payment.advanceInvoiceHint';
  };
  const hintKey = resolveHintKey();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodValue | undefined>(undefined);

  // Mark the preselected option once the block query resolves; guarded so a
  // background refetch never overrides a choice the user already made.
  const hasInitializedSelection = useRef(false);
  useEffect(() => {
    if (hasInitializedSelection.current || isLoadingBlock) return;
    hasInitializedSelection.current = true;
    setSelectedMethod(resolveInitialSelectedMethod(block, isReactivation));
  }, [block, isLoadingBlock, isReactivation]);

  // This step is the entry to the payment leg in every flow, so the funnel opens here and not at
  // the wizard's first route, which belongs to the block creation.
  const hasTrackedEntry = useRef(false);
  useEffect(() => {
    if (hasTrackedEntry.current || isLoadingBlock || !block) return;
    hasTrackedEntry.current = true;

    const origin = resolveWizardOrigin(search);
    trackPaymentStarted({
      blockUuid,
      customerState: block.customerState,
      daysToPaymentDate: getDaysToPaymentDate(block.paymentDate),
      hasPaymentMethod: block.paymentMethod !== null,
      origin,
    });
    trackPaymentStepViewed({ blockUuid, origin, step: WizardSteps.METHOD });
  }, [block, blockUuid, isLoadingBlock, search, trackPaymentStarted, trackPaymentStepViewed]);

  const handleContinue = () => {
    if (!selectedMethod) return;
    onContinue(selectedMethod);
  };

  return (
    <div className="flex h-[100dvh] flex-col items-center overflow-y-auto px-6 pb-12 pt-20 font-sans">
      <div className="flex w-full max-w-[1020px] flex-col gap-6 my-auto">
        <header className="flex flex-col gap-0.5">
          <h1 className="text-2xl font-semibold leading-9 text-foreground">{t('blockCreationWizard.payment.title')}</h1>
          <p className="text-base font-normal leading-6 text-foreground">
            {t('blockCreationWizard.payment.description')}
          </p>
        </header>

        {isLoadingBlock ? (
          <PaymentMethodCardsSkeleton />
        ) : (
          <RadioGroup
            className="grid grid-cols-1 gap-4 md:grid-cols-2"
            onValueChange={(value) => setSelectedMethod(value as PaymentMethodValue)}
            value={selectedMethod ?? ''}
          >
            <CardPaymentMethodCard
              disabled={isCardDisabled}
              isSelected={selectedMethod === CARD_PAYMENT_METHOD_VALUE}
            />
            <SpeiPaymentMethodCard
              disabled={isSpeiDisabled}
              isSelected={selectedMethod === SPEI_PAYMENT_METHOD_VALUE}
              showInvoiceNote={!isReactivation}
            />
          </RadioGroup>
        )}

        {!isLoadingBlock && hintKey && (
          <Alert className="flex items-center gap-2 bg-bg-info border-info text-info">
            <span className="flex shrink-0 items-center text-info!">
              <InfoIcon size={16} />
            </span>
            <AlertDescription className="text-sm">{t(hintKey)}</AlertDescription>
          </Alert>
        )}

        <WizardStepFooter>
          <Button
            className="w-full md:w-auto"
            disabled={isLoadingBlock || !selectedMethod || bothDisabled}
            onClick={handleContinue}
            type="button"
          >
            {t('blockCreationWizard.payment.submit')}
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </WizardStepFooter>
      </div>
    </div>
  );
};
